import Controller, { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { Task, task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import { isPresent } from '@ember/utils';
import UserPasswordValidations from 'cio/utils/validations/user-password';
import fade from 'ember-animated/transitions/fade';
import RouterService from '@ember/routing/router-service';
import SignupController from 'cio/pods/signup/controller';
import AnalyticsService from 'cio/services/analytics';

export default class SignupPasswordController extends Controller {
  validations!: FormValidations;

  @service router!: RouterService;
  @service analytics!: AnalyticsService;

  @controller('signup') signupController!: SignupController;

  _passwordTouched = false;
  _passwordConfirmationTouched = false;

  fade = fade;

  constructor() {
    super(...arguments);
    this.set(
      'validations',
      FormValidations.create({
        validations: UserPasswordValidations,
        context: this,
        modelKey: 'signupController.formValues',
      }),
    );
  }

  @computed('validations.errors.password')
  get _passwordHasError() {
    return isPresent((this.validations.errors as any).password);
  }

  setDefaults() {
    this.setProperties({
      _passwordTouched: Boolean(this.signupController.formValues.password),
      _passwordConfirmationTouched: Boolean(this.signupController.formValues.passwordConfirmation),
    });
  }

  _submitTask = task(async (validatePasswordTask: any, ev: MouseEvent) => {
    ev.preventDefault();

    await validatePasswordTask.perform(this.signupController.formValues.password);
    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    this.analytics.track('signup', 'password_submitted', {
      signupType: this.signupController.model.signupType.name,
    });
    this.signupController.goToNextStep();
  });

  @action
  onPasswordBlur() {
    this.set('_passwordTouched', this._passwordTouched || Boolean(this.signupController.formValues.password));
  }

  @action
  onConfirmPasswordInput(value: string) {
    this.signupController.setFormValue('passwordConfirmation', value);
    this.set('_passwordConfirmationTouched', true);
    this.validations.validateAttribute('passwordConfirmation');
  }

  @action
  onPasswordChange(validationTask: Task<unknown, [string]>, password: string) {
    this.signupController.setFormValue('password', password);
    validationTask.perform(password);
    this.validations.validateAttribute('password');
  }
}
